import * as React from "react";
import { getWindowJobsiteConfig } from "../types/runtime-config";

const TURNSTILE_SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";

/**
 * Mounts a Cloudflare Turnstile widget into the container and stores the
 * challenge token in tokenRef.current via CF's callback.
 *
 * `containerRef` is a CALLBACK ref, not an object ref: the container often
 * mounts later than the hook (e.g. the booking widget's Turnstile lives in the
 * phone step, several steps after the widget mounts). A callback ref re-runs the
 * render effect exactly when the node attaches, so the widget always renders —
 * an object ref would be null at mount and Turnstile would never appear.
 *
 * Works in both SSG (script injected by prerenderPages) and local dev (script
 * injected on demand). No-op when turnstileSiteKey is absent.
 */
const useTurnstile = () => {
  const tokenRef = React.useRef<string | undefined>(undefined);
  const [container, setContainer] = React.useState<HTMLDivElement | null>(null);
  const containerRef = React.useCallback((node: HTMLDivElement | null) => {
    setContainer(node);
  }, []);

  // Reserve the widget's footprint (CF's managed widget is 300x65) as soon as
  // We know the site enforces Turnstile, so the form doesn't jump when the
  // Challenge pops in. Post-mount state (not render-time) to match SSR markup.
  const [hasWidget, setHasWidget] = React.useState(false);
  React.useEffect(() => {
    setHasWidget(Boolean(getWindowJobsiteConfig()?.turnstileSiteKey));
  }, []);
  const containerClassName = hasWidget ? "jobsite:min-h-[65px]" : undefined;

  React.useEffect(() => {
    const siteKey = getWindowJobsiteConfig()?.turnstileSiteKey;
    if (!siteKey || !container) {
      return;
    }

    const w = window as unknown as { turnstile?: any };
    let poll: ReturnType<typeof setInterval> | undefined,
      widgetId: string | undefined;

    const doRender = () => {
      if (widgetId !== undefined || !w.turnstile) {
        return;
      }
      widgetId = w.turnstile.render(container, {
        sitekey: siteKey,
        callback: (token: string) => {
          tokenRef.current = token;
        },
        "expired-callback": () => {
          tokenRef.current = undefined;
        },
        "error-callback": () => {
          tokenRef.current = undefined;
        },
      });
    };

    if (w.turnstile) {
      doRender();
    } else {
      // SSG builds inject the script via prerenderPages; local dev doesn't.
      if (!document.querySelector(`script[src*="turnstile"]`)) {
        const script = document.createElement("script");
        script.src = TURNSTILE_SCRIPT_URL;
        script.async = true;
        document.head.appendChild(script);
      }
      poll = setInterval(() => {
        if (w.turnstile) {
          clearInterval(poll);
          doRender();
        }
      }, 50);
    }

    return () => {
      if (poll) {
        clearInterval(poll);
      }
      if (widgetId !== undefined) {
        try {
          w.turnstile?.remove(widgetId);
        } catch {
          /* Widget already gone */
        }
      }
      tokenRef.current = undefined;
    };
  }, [container]);

  return { containerClassName, containerRef, tokenRef };
};

export { useTurnstile };
